import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import {
  Plus, Search, LayoutGrid, List, ChevronRight, MapPin,
  Zap, Calendar, DollarSign, Clock, CheckCircle2, AlertCircle,
  Palette, ArrowRight, ArrowLeft, User, Trash2
} from 'lucide-react';

// ── Pipeline stage config ────────────────────────────────────────────────────
const STAGES = [
  {
    key: 'pending',
    label: 'New Sale',
    description: 'Review inventory & color',
    color: 'border-t-amber-400',
    headerBg: 'bg-amber-50',
    dot: 'bg-amber-400',
    badge: 'bg-amber-100 text-amber-800',
  },
  {
    key: 'color_match',
    label: 'Color Match',
    description: 'Waiting on color matching',
    color: 'border-t-purple-400',
    headerBg: 'bg-purple-50',
    dot: 'bg-purple-400',
    badge: 'bg-purple-100 text-purple-800',
  },
  {
    key: 'confirmed',
    label: 'Confirmed',
    description: 'Contact customer for install date',
    color: 'border-t-blue-400',
    headerBg: 'bg-blue-50',
    dot: 'bg-blue-400',
    badge: 'bg-blue-100 text-blue-800',
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    description: 'Install date locked in',
    color: 'border-t-indigo-500',
    headerBg: 'bg-indigo-50',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-800',
  },
  {
    key: 'in_progress',
    label: 'Day of Install',
    description: 'Currently being installed',
    color: 'border-t-orange-400',
    headerBg: 'bg-orange-50',
    dot: 'bg-orange-400',
    badge: 'bg-orange-100 text-orange-800',
  },
  {
    key: 'awaiting_payment',
    label: 'Awaiting Payment',
    description: 'Installed — collecting payment',
    color: 'border-t-rose-400',
    headerBg: 'bg-rose-50',
    dot: 'bg-rose-400',
    badge: 'bg-rose-100 text-rose-800',
  },
  {
    key: 'completed',
    label: 'Installed & Collected',
    description: 'Payment received',
    color: 'border-t-green-500',
    headerBg: 'bg-green-50',
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-800',
  },
];

// Next stage action label
const NEXT_ACTION = {
  pending:          { label: 'Confirm',        next: null },        // special: may go to color_match
  color_match:      { label: 'Match Received', next: 'confirmed' },
  confirmed:        { label: 'Mark Scheduled', next: 'scheduled' },
  scheduled:        { label: 'Start Install',  next: 'in_progress' },
  in_progress:      { label: 'Mark Installed', next: 'awaiting_payment' },
  awaiting_payment: { label: 'Collect Payment',next: null, goToDetail: true }, // requires payment entry in detail
};

// Previous stage for moving backwards (one step at a time)
const PREV_STAGE = {
  color_match:      'pending',
  confirmed:        'color_match',
  scheduled:        'confirmed',
  in_progress:      'scheduled',
  awaiting_payment: 'in_progress',
  completed:        'awaiting_payment',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 0 });
const payLabel = { cash: 'Cash', etransfer: 'E-Transfer', cc: 'CC', financed: 'Financed' };
const payColor = {
  cash: 'bg-green-100 text-green-700',
  etransfer: 'bg-blue-100 text-blue-700',
  cc: 'bg-violet-100 text-violet-700',
  financed: 'bg-orange-100 text-orange-700',
};

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - d) / 86400000);
}

function DaysChip({ created_at }) {
  const days = daysAgo(created_at);
  if (days === null) return null;
  const color = days > 14 ? 'text-red-500' : days > 7 ? 'text-amber-500' : 'text-gray-400';
  return (
    <span className={`flex items-center gap-0.5 text-xs ${color}`}>
      <Clock className="w-3 h-3" />
      {days === 0 ? 'Today' : `${days}d`}
    </span>
  );
}

// ── Sale Card ────────────────────────────────────────────────────────────────
function SaleCard({ sale, stageKey, isAdmin, onStatusChange }) {
  const navigate = useNavigate();
  const [actioning, setActioning] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${sale.customer_name}'s sale? This cannot be undone.`)) return;
    try {
      await api.delete(`/sales/${sale.id}`);
      onStatusChange();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const handleAction = async (e) => {
    e.stopPropagation();
    const action = NEXT_ACTION[stageKey];
    if (!action) return;

    // Some actions require going to the sale detail (e.g. payment entry)
    if (action.goToDetail) {
      navigate(`/sales/${sale.id}`);
      return;
    }

    let nextStatus = action.next;
    // pending → has color = confirmed, no color = color_match
    if (stageKey === 'pending') {
      nextStatus = sale.channel_color_id ? 'confirmed' : 'color_match';
    }

    // Require color when moving from color_match → confirmed
    if (stageKey === 'color_match' && nextStatus === 'confirmed' && !sale.channel_color_id) return;

    setActioning(true);
    try {
      await api.patch(`/sales/${sale.id}/status`, { status: nextStatus });
      onStatusChange();
    } finally {
      setActioning(false);
    }
  };

  const handleMoveBack = async (e) => {
    e.stopPropagation();
    const prevStage = PREV_STAGE[stageKey];
    if (!prevStage) return;
    setActioning(true);
    try {
      await api.patch(`/sales/${sale.id}/status`, { status: prevStage });
      onStatusChange();
    } finally {
      setActioning(false);
    }
  };

  const action = NEXT_ACTION[stageKey];
  const remaining = (sale.deal_total || 0) - (sale.total_paid || 0);

  // Color required only when moving from color_match → confirmed
  const missingColor = stageKey === 'color_match' && !sale.channel_color_id;

  return (
    <div
      onClick={() => navigate(`/sales/${sale.id}`)}
      className="bg-white rounded-lg border border-gray-200 p-3.5 cursor-pointer hover:border-nova-300 hover:shadow-sm transition-all group"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-gray-900 truncate">{sale.customer_name}</p>
          {sale.city && (
            <p className="text-xs text-gray-400 flex items-center gap-0.5 mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{sale.city}</span>
            </p>
          )}
        </div>
        <DaysChip created_at={sale.updated_at || sale.created_at} />
      </div>

      {/* Job info row */}
      <div className="flex flex-wrap gap-1.5 mb-2.5">
        {sale.total_footage > 0 && (
          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
            {sale.total_footage} ft
          </span>
        )}
        {sale.color_name && (
          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
            <Palette className="w-3 h-3" />{sale.color_name}
          </span>
        )}
        {sale.payment_type && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${payColor[sale.payment_type] || 'bg-gray-100 text-gray-600'}`}>
            {payLabel[sale.payment_type] || sale.payment_type}
          </span>
        )}
      </div>

      {/* Financial row */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-sm font-bold text-gray-900">{fmt(sale.deal_total)}</span>
        {/* Payment status — meaningful post-install */}
        {(stageKey === 'in_progress' || stageKey === 'awaiting_payment' || stageKey === 'completed') && (() => {
          const isFinanced = sale.payment_type === 'financed';
          const rotessaActive = isFinanced && !!sale.finance_start_date;
          const rotessaNeeded = isFinanced && !sale.finance_start_date;
          const paid = remaining <= 0.5;

          if (paid && !isFinanced) {
            return (
              <span className="text-xs text-green-600 font-medium flex items-center gap-0.5">
                <CheckCircle2 className="w-3 h-3" /> Paid ✓
              </span>
            );
          }
          if (rotessaActive) {
            return (
              <span className="text-xs text-purple-600 font-medium flex items-center gap-0.5">
                <CheckCircle2 className="w-3 h-3" /> Billing active
              </span>
            );
          }
          if (rotessaNeeded) {
            return (
              <span className="text-xs text-orange-500 font-medium flex items-center gap-0.5">
                <AlertCircle className="w-3 h-3" /> Setup Rotessa
              </span>
            );
          }
          if (remaining > 0.5) {
            return (
              <span className="text-xs text-red-500 font-medium flex items-center gap-0.5">
                <AlertCircle className="w-3 h-3" /> {fmt(remaining)} owing
              </span>
            );
          }
          return null;
        })()}
      </div>

      {/* Schedule info for scheduled stage */}
      {sale.scheduled_date && (() => {
        const d = new Date(sale.scheduled_date + 'T00:00:00');
        const today = new Date(); today.setHours(0,0,0,0);
        const diffDays = Math.round((d - today) / 86400000);
        const isToday = diffDays === 0;
        const isTomorrow = diffDays === 1;
        const isOverdue = diffDays < 0;
        const label = isToday ? 'Today' : isTomorrow ? 'Tomorrow'
          : isOverdue ? `${Math.abs(diffDays)}d ago`
          : d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', weekday: 'short' });
        return (
          <div className={`text-xs flex items-center gap-1 mb-2.5 font-medium px-2 py-0.5 rounded-full w-fit ${
            isOverdue ? 'bg-red-100 text-red-600'
            : isToday ? 'bg-green-100 text-green-700'
            : isTomorrow ? 'bg-blue-100 text-blue-700'
            : 'bg-indigo-50 text-indigo-600'
          }`}>
            <Calendar className="w-3 h-3" />
            {label}
          </div>
        );
      })()}

      {/* Ideal date for confirmed */}
      {stageKey === 'confirmed' && sale.ideal_install_date && (
        <div className="text-xs text-blue-500 flex items-center gap-1 mb-2.5">
          <Calendar className="w-3 h-3" />
          Ideal: {new Date(sale.ideal_install_date + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
          {sale.flexible_schedule ? ' · Flexible' : ''}
        </div>
      )}

      {/* Color match status */}
      {stageKey === 'color_match' && (
        <div className="text-xs text-purple-600 mb-2.5">
          Match: {sale.color_match_status?.replace(/_/g, ' ') || 'pending'}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {isAdmin && sale.salesman_name ? (
          <span className="text-xs text-gray-400 flex items-center gap-0.5">
            <User className="w-3 h-3" />{sale.salesman_name.split(' ')[0]}
          </span>
        ) : <span />}

        {isAdmin && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleDelete}
              title="Delete sale"
              className="text-gray-300 hover:text-red-500 hover:bg-red-50 rounded p-1 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            {PREV_STAGE[stageKey] && (
              <button
                onClick={handleMoveBack}
                disabled={actioning}
                title="Move back one stage"
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded p-1 disabled:opacity-40"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
            )}
            {action && (
              missingColor ? (
                <span className="text-xs text-amber-500 font-medium flex items-center gap-0.5">
                  <AlertCircle className="w-3 h-3" /> Add color first
                </span>
              ) : (
                <button
                  onClick={handleAction}
                  disabled={actioning}
                  className="text-xs font-medium text-nova-600 hover:text-nova-800 flex items-center gap-0.5"
                >
                  {actioning ? '…' : action.label} <ArrowRight className="w-3 h-3" />
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Kanban Column ────────────────────────────────────────────────────────────
function KanbanColumn({ stage, sales, isAdmin, onStatusChange, search }) {
  const filtered = sales.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.customer_name?.toLowerCase().includes(q) ||
      s.city?.toLowerCase().includes(q) ||
      s.salesman_name?.toLowerCase().includes(q) ||
      s.color_name?.toLowerCase().includes(q)
    );
  });

  const totalValue = filtered.reduce((sum, s) => sum + (s.deal_total || 0), 0);

  return (
    <div className={`flex-shrink-0 w-64 bg-gray-50 rounded-xl border-t-4 ${stage.color} flex flex-col`} style={{ minHeight: 200 }}>
      {/* Column header */}
      <div className={`px-3 pt-3 pb-2 rounded-t-xl ${stage.headerBg}`}>
        <div className="flex items-center justify-between mb-0.5">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
            <span className="text-sm font-semibold text-gray-800">{stage.label}</span>
          </div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stage.badge}`}>
            {filtered.length}
          </span>
        </div>
        <p className="text-xs text-gray-500 ml-4">{stage.description}</p>
        {filtered.length > 0 && (
          <p className="text-xs text-gray-400 ml-4 mt-0.5">{fmt(totalValue)}</p>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
        {filtered.map(s => (
          <SaleCard
            key={s.id}
            sale={s}
            stageKey={stage.key}
            isAdmin={isAdmin}
            onStatusChange={onStatusChange}
          />
        ))}
        {filtered.length === 0 && (
          <div className="text-xs text-gray-300 text-center py-6">No sales</div>
        )}
      </div>
    </div>
  );
}

// ── List Row ─────────────────────────────────────────────────────────────────
function ListRow({ sale, isAdmin }) {
  const navigate = useNavigate();
  const stage = STAGES.find(s => s.key === sale.status);
  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => navigate(`/sales/${sale.id}`)}
    >
      <td className="px-4 py-3">
        <p className="font-medium text-sm">{sale.customer_name}</p>
        <p className="text-xs text-gray-400 flex items-center gap-0.5">
          <MapPin className="w-3 h-3" />{sale.city || '—'}
        </p>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${stage?.badge || 'bg-gray-100 text-gray-600'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${stage?.dot || 'bg-gray-400'}`} />
          {stage?.label || sale.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm">{sale.total_footage > 0 ? `${sale.total_footage} ft` : '—'}</td>
      <td className="px-4 py-3 text-sm">{sale.color_name || '—'}</td>
      <td className="px-4 py-3 text-sm font-semibold">{fmt(sale.deal_total)}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-1.5 py-0.5 rounded ${payColor[sale.payment_type] || 'bg-gray-100 text-gray-600'}`}>
          {payLabel[sale.payment_type] || sale.payment_type}
        </span>
      </td>
      {isAdmin && (
        <td className="px-4 py-3 text-xs text-gray-500">{sale.salesman_name}</td>
      )}
      <td className="px-4 py-3">
        <DaysChip created_at={sale.updated_at || sale.created_at} />
      </td>
      <td className="px-4 py-3">
        <ChevronRight className="w-4 h-4 text-gray-300" />
      </td>
    </tr>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SalesList() {
  const { user } = useAuth();
  const isAdmin = user.role === 'admin' || user.role === 'manager';

  const [sales, setSales] = useState([]);
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [search, setSearch] = useState('');
  const [salesmanFilter, setSalesmanFilter] = useState('');
  const [salesmen, setSalesmen] = useState([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const kanbanRef = useRef(null);

  useEffect(() => {
    const el = kanbanRef.current;
    if (!el) return;
    const handler = (e) => {
      // Only hijack pure vertical scrolls; let horizontal (trackpad swipe) pass through
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [view]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (salesmanFilter) params.set('salesman_id', salesmanFilter);
    // fetch all statuses; we filter client-side into columns
    api.get(`/sales?${params}`).then(setSales);
  }, [salesmanFilter]);

  useEffect(() => {
    if (isAdmin) api.get('/users/salesmen').then(setSalesmen);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Bucket sales into stage groups
  const byStage = {};
  STAGES.forEach(s => { byStage[s.key] = []; });
  const cancelled = [];
  sales.forEach(s => {
    if (s.status === 'cancelled') { cancelled.push(s); return; }
    if (byStage[s.status]) byStage[s.status].push(s);
  });

  // Sort "scheduled" column by install date ascending (soonest first)
  byStage['scheduled'].sort((a, b) => {
    if (!a.scheduled_date && !b.scheduled_date) return 0;
    if (!a.scheduled_date) return 1;
    if (!b.scheduled_date) return -1;
    return a.scheduled_date.localeCompare(b.scheduled_date);
  });

  // Pipeline summary totals
  const activeSales = sales.filter(s => s.status !== 'cancelled' && s.status !== 'completed');
  const pipelineValue = activeSales.reduce((sum, s) => sum + (s.deal_total || 0), 0);
  const completedThisMonth = sales.filter(s => {
    if (s.status !== 'completed') return false;
    const d = new Date(s.updated_at || s.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const completedRevenue = completedThisMonth.reduce((sum, s) => sum + (s.deal_total || 0), 0);

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold">Sales Pipeline</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {activeSales.length} active · {fmt(pipelineValue)} in pipeline ·{' '}
            <span className="text-green-600">{fmt(completedRevenue)} installed this month</span>
          </p>
        </div>
        <Link
          to="/sales/new"
          className="bg-nova-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-nova-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Sale
        </Link>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-shrink-0 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-nova-500 bg-white"
            placeholder="Search customer, city…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {isAdmin && (
          <select
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            value={salesmanFilter}
            onChange={e => setSalesmanFilter(e.target.value)}
          >
            <option value="">All salesmen</option>
            {salesmen.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        )}

        {/* View toggle */}
        <div className="flex border rounded-lg overflow-hidden ml-auto">
          <button
            onClick={() => setView('kanban')}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === 'kanban' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <LayoutGrid className="w-4 h-4" /> Pipeline
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === 'list' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <List className="w-4 h-4" /> List
          </button>
        </div>
      </div>

      {/* ── KANBAN VIEW ────────────────────────────────────────────────────── */}
      {view === 'kanban' && (
        <div
          ref={kanbanRef}
          className="overflow-x-auto pb-2"
          style={{ height: 'calc(100vh - 200px)' }}
        >
          <div className="flex gap-3 h-full" style={{ minWidth: `${STAGES.length * 272}px` }}>
            {STAGES.map(stage => (
              <KanbanColumn
                key={stage.key}
                stage={stage}
                sales={byStage[stage.key] || []}
                isAdmin={isAdmin}
                onStatusChange={load}
                search={search}
              />
            ))}
          </div>

          {/* Cancelled toggle */}
          {isAdmin && cancelled.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowCancelled(v => !v)}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
              >
                {showCancelled ? '▾' : '▸'} {cancelled.length} cancelled sale{cancelled.length !== 1 ? 's' : ''}
              </button>
              {showCancelled && (
                <div className="mt-2 flex gap-3 flex-wrap">
                  {cancelled
                    .filter(s => !search || s.customer_name?.toLowerCase().includes(search.toLowerCase()))
                    .map(s => (
                      <Link
                        key={s.id}
                        to={`/sales/${s.id}`}
                        className="bg-white border border-dashed border-gray-300 rounded-lg p-3 text-sm text-gray-500 hover:border-gray-400 w-64"
                      >
                        <p className="font-medium">{s.customer_name}</p>
                        <p className="text-xs">{s.city} · {fmt(s.deal_total)}</p>
                      </Link>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── LIST VIEW ──────────────────────────────────────────────────────── */}
      {view === 'list' && (
        <div className="bg-white rounded-xl border overflow-hidden flex-1">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-500">Customer</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Stage</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Footage</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Color</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Deal Total</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Payment</th>
                  {isAdmin && <th className="px-4 py-3 font-medium text-gray-500">Salesman</th>}
                  <th className="px-4 py-3 font-medium text-gray-500">Age</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {sales
                  .filter(s => {
                    if (!search) return true;
                    const q = search.toLowerCase();
                    return (
                      s.customer_name?.toLowerCase().includes(q) ||
                      s.city?.toLowerCase().includes(q) ||
                      s.salesman_name?.toLowerCase().includes(q)
                    );
                  })
                  .map(s => (
                    <ListRow key={s.id} sale={s} isAdmin={isAdmin} />
                  ))}
                {sales.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-400">No sales found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
